import "@/App.css";
import { useEffect, useLayoutEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import { Toaster } from "@/components/ui/sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Properties from "@/pages/Properties";
import PropertyDetail from "@/pages/PropertyDetail";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import FaqPage from "@/pages/FaqPage";
import Terms from "@/pages/Terms";
import BookingPolicies from "@/pages/BookingPolicies";
import HostSubscription from "@/pages/HostSubscription";
import HostRegister from "@/pages/HostRegister";
import HostSubscriptionConfirmed from "@/pages/HostSubscriptionConfirmed";
import HostSubscriptionCancelled from "@/pages/HostSubscriptionCancelled";
import HostProfile from "@/pages/HostProfile";
import BookingReview from "@/pages/BookingReview";
import BookingConfirmed from "@/pages/BookingConfirmed";
import BookingCancelled from "@/pages/BookingCancelled";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import VerifyOtp from "@/pages/auth/VerifyOtp";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import AccountLayout from "@/components/AccountLayout";
import Dashboard from "@/pages/account/Dashboard";
import Profile from "@/pages/account/Profile";
import Bookings from "@/pages/account/Bookings";
import Messages from "@/pages/account/Messages";
import Conversation from "@/pages/account/Conversation";
import Reviews from "@/pages/account/Reviews";

const RouteScrollManager = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!("scrollRestoration" in window.history)) return undefined;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  useLayoutEffect(() => {
    if (hash) {
      const scrollToTarget = () => {
        const target = document.getElementById(decodeURIComponent(hash.slice(1)));
        if (target) {
          target.scrollIntoView({ block: "start", behavior: "auto" });
          return true;
        }
        return false;
      };

      if (!scrollToTarget()) {
        window.requestAnimationFrame(() => {
          if (!scrollToTarget()) window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        });
      }
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);

  return null;
};

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <RouteScrollManager />
        <div className="App min-h-screen bg-background text-foreground">
          <Nav />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/properties" element={<Properties />} />
            <Route path="/properties/:id" element={<PropertyDetail />} />
            <Route path="/hosts/:hostId/:slug" element={<HostProfile />} />
            <Route path="/hosts/subscription" element={<HostSubscription />} />
            <Route path="/hosts/register" element={<HostRegister />} />
            <Route path="/host/subscription-confirmed" element={<HostSubscriptionConfirmed />} />
            <Route path="/host/subscription-cancelled" element={<HostSubscriptionCancelled />} />
            <Route path="/booking/review/:propertySlug" element={<BookingReview />} />
            <Route path="/booking-confirmed" element={<BookingConfirmed />} />
            <Route path="/booking-cancelled" element={<BookingCancelled />} />
            <Route path="/about-us" element={<About />} />
            <Route path="/contact-us" element={<Contact />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/terms-and-conditions" element={<Terms />} />
            <Route path="/booking-policies" element={<BookingPolicies />} />

            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email-otp" element={<VerifyOtp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route path="/account" element={<AccountLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="profile" element={<Profile />} />
              <Route path="bookings" element={<Bookings />} />
              <Route path="messages" element={<Messages />} />
              <Route path="bookings/:bookingId/messages" element={<Conversation />} />
              <Route path="reviews" element={<Reviews />} />
            </Route>
          </Routes>
          <Footer />
        </div>
        <Toaster position="bottom-right" toastOptions={{ style: { background: "hsl(var(--foreground))", color: "hsl(var(--background))", border: "none", borderRadius: "999px" } }} />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
